import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import DarkModeToggle from './DarkModeToggle'
import { smoothAnimations } from '../utils/animations'
import logo from '../assets/logo.png'

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isProductsOpen, setIsProductsOpen] = useState(false)

  const navItems = [
    { name: 'Home', href: '#home' },
    { name: 'About', href: '#about' },
    { 
      name: 'Products', 
      href: '#products',
      hasDropdown: true,
      dropdownItems: [
        { name: 'VeltraPay Solutions', href: '#products', description: 'Payment Gateway Platform' },
        { name: 'ILU Wallet', href: '#products', description: 'Digital Wallet App' },
        { name: 'All Products', href: '#products', description: 'View Complete Suite' }
      ]
    },
    { name: 'Features', href: '#features' },
    { name: 'Values', href: '#values' },
    { name: 'Contact', href: '#contact' },
  ]

  const handleProductClick = (item) => {
    if (item.hasDropdown) {
      setIsProductsOpen(!isProductsOpen)
    } else {
      window.location.href = item.href
    }
  }

  return (
    <motion.header 
      className="fixed top-0 w-full bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm shadow-lg z-50 transition-colors duration-300"
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <div className="container mx-auto px-6">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <motion.div 
            className="flex items-center space-x-3"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <img src={logo} alt="Orifel Technologies" className="w-10 h-10 rounded-lg" />
            <div>
              <h1 className="text-lg lg:text-xl font-bold text-gray-900 dark:text-white">Orifel Technologies</h1>
              <p className="hidden lg:flex text-sm text-gray-600 dark:text-gray-300">Your Vision, Delivered</p>
            </div>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex space-x-8 items-center">
            {navItems.map((item, index) => (
              <div key={item.name} className="relative">
                {item.hasDropdown ? (
                  <div 
                    className="relative"
                    onMouseEnter={() => setIsProductsOpen(true)}
                    onMouseLeave={() => setIsProductsOpen(false)}
                  >
                    <motion.button
                      className="text-gray-700 dark:text-gray-300 hover:text-orifel dark:hover:text-orifel font-medium transition-colors duration-300 flex items-center"
                      whileHover={{ scale: 1.1 }}
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 + 0.3 }}
                    >
                      {item.name}
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </motion.button>

                    <AnimatePresence>
                      {isProductsOpen && (
                        <motion.div
                          className="absolute top-full left-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-2"
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                        >
                          {item.dropdownItems.map((dropdownItem, idx) => (
                            <motion.a
                              key={idx}
                              href={dropdownItem.href}
                              className="block px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                              whileHover={{ x: 5 }}
                            >
                              <div className="text-gray-900 dark:text-white font-medium">{dropdownItem.name}</div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">{dropdownItem.description}</div>
                            </motion.a>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <motion.a
                    href={item.href}
                    className="text-gray-700 dark:text-gray-300 hover:text-orifel dark:hover:text-orifel font-medium transition-colors duration-300"
                    whileHover={{ scale: 1.1 }}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 + 0.3 }}
                  >
                    {item.name}
                  </motion.a>
                )}
              </div>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center space-x-4">
            <DarkModeToggle />
            <motion.a
              href="#contact"
              className="bg-orifel text-white px-6 py-2 rounded-lg font-semibold hover:bg-purple-700 transition duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 }}
            >
              Get Started
            </motion.a>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center space-x-3">
            <DarkModeToggle />
            <motion.button
              className="p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              whileTap={{ scale: 0.9 }}
            >
              <div className="w-6 h-6 flex flex-col justify-center items-center">
                <motion.div
                  className="w-6 h-0.5 bg-gray-600 dark:bg-gray-300 mb-1"
                  animate={{ rotate: isMenuOpen ? 45 : 0, y: isMenuOpen ? 6 : 0 }}
                  transition={{ duration: 0.3 }}
                />
                <motion.div
                  className="w-6 h-0.5 bg-gray-600 dark:bg-gray-300 mb-1"
                  animate={{ opacity: isMenuOpen ? 0 : 1 }}
                  transition={{ duration: 0.3 }}
                />
                <motion.div
                  className="w-6 h-0.5 bg-gray-600 dark:bg-gray-300"
                  animate={{ rotate: isMenuOpen ? -45 : 0, y: isMenuOpen ? -6 : 0 }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </motion.button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.nav
              className="lg:hidden bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="px-6 py-4 space-y-4">
                {navItems.map((item, index) => (
                  <div key={item.name}>
                    {item.hasDropdown ? (
                      <div>
                        <motion.button
                          className="block w-full text-left text-gray-700 dark:text-gray-300 hover:text-orifel dark:hover:text-orifel font-medium transition-colors duration-300 py-2 px-2"
                          onClick={() => handleProductClick(item)}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <div className="flex items-center justify-between">
                            <span>{item.name}</span>
                            <svg 
                              className={`w-4 h-4 transition-transform duration-200 ${isProductsOpen ? 'rotate-180' : ''}`} 
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                            </svg>
                          </div>
                        </motion.button>
                        <AnimatePresence>
                          {isProductsOpen && (
                            <motion.div
                              className="pl-6 mt-2 space-y-2 bg-gray-50 dark:bg-gray-700 rounded-lg py-3"
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                            >
                              {item.dropdownItems.map((dropdownItem, idx) => (
                                <motion.a
                                  key={idx}
                                  href={dropdownItem.href}
                                  className="block text-gray-600 dark:text-gray-400 hover:text-orifel py-2 px-2 rounded transition-colors duration-200"
                                  onClick={() => setIsMenuOpen(false)}
                                  whileHover={{ x: 5 }}
                                >
                                  <div className="font-medium">{dropdownItem.name}</div>
                                  <div className="text-sm text-gray-500 dark:text-gray-500">{dropdownItem.description}</div>
                                </motion.a>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <motion.a
                        href={item.href}
                        className="block text-gray-700 dark:text-gray-300 hover:text-orifel dark:hover:text-orifel font-medium transition-colors duration-300 py-2 px-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                        onClick={() => setIsMenuOpen(false)}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        {item.name}
                      </motion.a>
                    )}
                  </div>
                ))}
                <motion.a
                  href="#contact"
                  className="block bg-orifel text-white px-6 py-3 rounded-lg font-semibold text-center hover:bg-purple-700 transition duration-300 mt-6 mx-2"
                  onClick={() => setIsMenuOpen(false)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  data-analytics="cta"
                >
                  Get Started
                </motion.a>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  )
}

export default Header
